import React, { useState, useEffect, useRef } from "react";
import styles from "./styles/CreditCalculator.module.css";
import QRCode from "react-qr-code";
import googleplay from "../assets/images/googleplay.png";
import appstore from "../assets/images/appstore.png";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../LanguageContext";

function CreditCalculator({ appLinks }) {
    const { t } = useLanguage();
    const [loanAmount, setLoanAmount] = useState(100000);
    const [initialPaymentPercent, setInitialPaymentPercent] = useState(0);
    const [leaseTerm, setLeaseTerm] = useState(36);

    const initialPayment = (loanAmount * initialPaymentPercent) / 100;
    const totalAmountDue = loanAmount - initialPayment;
    const serviceFeeRate = 0.025;

    // Monthly payment as in Excel-style (base + service fee)
    const monthlyPayment = totalAmountDue / leaseTerm + totalAmountDue * serviceFeeRate;

    // Total Payments and Interest
    const totalPayments = Math.round(monthlyPayment * leaseTerm);
    const totalInterest = Math.round(totalPayments - totalAmountDue);

    const [hoveredTooltip, setHoveredTooltip] = useState(null);

    useEffect(() => {
        const sliders = document.querySelectorAll('input[type="range"]');

        sliders.forEach((slider) => {
            const updateSlider = () => {
                const value = ((slider.value - slider.min) / (slider.max - slider.min)) * 100;
                slider.style.setProperty("--progress", `${value}%`);
            };
            slider.addEventListener("input", updateSlider);
            updateSlider(); // initial
        });
    }, []);

    const loanAmountRangeRef = useRef(null);
    const leaseTermRangeRef = useRef(null);

    const updateSliderProgress = (inputEl) => {
        if (!inputEl) return;
        const value = ((inputEl.value - inputEl.min) / (inputEl.max - inputEl.min)) * 100;
        inputEl.style.setProperty("--progress", `${value}%`);
    };

    useEffect(() => {
        if (leaseTermRangeRef.current) {
            leaseTermRangeRef.current.value = leaseTerm;
            updateSliderProgress(leaseTermRangeRef.current);
        }
    }, [leaseTerm]);

    return (
        <div className={styles.calculatorWrapper}>
            <div className={styles.inputSection}>
                <h1>{t("calculator.title")}</h1>

                <label>
                    <p>{t("calculator.costLabel")}</p>
                    <div className={styles.inputNumberLikeSpan}>
                        <input
                            type="text"
                            value={loanAmount === 0 ? "" : loanAmount.toString()}
                            onChange={(e) => {
                                let raw = e.target.value.replace(/\D/g, "");
                                if (raw === "") {
                                    setLoanAmount(0);
                                } else {
                                    const num = Number(raw);
                                    const validValue = Math.min(num, 1000000);
                                    setLoanAmount(validValue);

                                    // Also update the slider's CSS variable
                                    if (loanAmountRangeRef.current) {
                                        loanAmountRangeRef.current.value = validValue;
                                        updateSliderProgress(loanAmountRangeRef.current);
                                    }
                                }
                            }}
                        />
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1 7C1 5.4087 1.63214 3.88258 2.75736 2.75736C3.88258 1.63214 5.4087 1 7 1C8.5913 1 10.1174 1.63214 11.2426 2.75736C12.3679 3.88258 13 5.4087 13 7V17M9 13H17M9 9H17"
                                stroke="black"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    {/* <span>
                        {loanAmount}
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1 7C1 5.4087 1.63214 3.88258 2.75736 2.75736C3.88258 1.63214 5.4087 1 7 1C8.5913 1 10.1174 1.63214 11.2426 2.75736C12.3679 3.88258 13 5.4087 13 7V17M9 13H17M9 9H17"
                                stroke="black"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span> */}
                </label>
                <input
                    ref={loanAmountRangeRef}
                    type="range"
                    min="100000"
                    max="1000000"
                    step="10000"
                    value={loanAmount}
                    onChange={(e) => {
                        const value = Number(e.target.value);
                        setLoanAmount(value);
                        updateSliderProgress(e.target);
                    }}
                />
                <div className={styles.valueDisplay}>
                    <span>
                        {" "}
                        100000{" "}
                        <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1 5.25C1 4.25544 1.39509 3.30161 2.09835 2.59835C2.80161 1.89509 3.75544 1.5 4.75 1.5C5.74456 1.5 6.69839 1.89509 7.40165 2.59835C8.10491 3.30161 8.5 4.25544 8.5 5.25V11.5M6 9H11M6 6.5H11"
                                stroke="white"
                                stroke-width="1.2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span>

                    <span>
                        1 000 000{" "}
                        <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1 5.25C1 4.25544 1.39509 3.30161 2.09835 2.59835C2.80161 1.89509 3.75544 1.5 4.75 1.5C5.74456 1.5 6.69839 1.89509 7.40165 2.59835C8.10491 3.30161 8.5 4.25544 8.5 5.25V11.5M6 9H11M6 6.5H11"
                                stroke="white"
                                stroke-width="1.2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                </div>

                {/* <label>
                    <p>{t("calculator.initialPaymentLabel")}</p>
                    <div className={styles.initialPaymentWrapper}>
                        <span className={styles.initialPayment}>{initialPaymentPercent} %</span>
                        <span>
                            {initialPayment.toLocaleString()}
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M1 7C1 5.4087 1.63214 3.88258 2.75736 2.75736C3.88258 1.63214 5.4087 1 7 1C8.5913 1 10.1174 1.63214 11.2426 2.75736C12.3679 3.88258 13 5.4087 13 7V17M9 13H17M9 9H17"
                                    stroke="black"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </span>
                    </div>
                </label>
                <input
                    type="range"
                    min="0"
                    max="61"
                    value={initialPaymentPercent}
                    onChange={(e) => setInitialPaymentPercent(Number(e.target.value))}
                />
                <div className={styles.valueDisplay}>
                    <span>0 %</span>

                    <span>61 %</span>
                </div>*/}

                <label>
                    <p>{t("calculator.leaseTermLabel")}</p>

                    <div className={styles.inputNumberLikeSpan}>
                        <input
                            type="text"
                            value={leaseTerm === 0 ? "" : leaseTerm.toString()}
                            onChange={(e) => {
                                const raw = e.target.value.replace(/\D/g, "");
                                setLeaseTerm(raw === "" ? 0 : Number(raw)); // Let user type anything
                            }}
                            onBlur={(e) => {
                                const raw = e.target.value.replace(/\D/g, "");
                                if (raw === "") {
                                    setLeaseTerm(12); // Default to min if empty
                                } else {
                                    const num = Number(raw);
                                    const clamped = Math.min(Math.max(num, 12), 36);
                                    setLeaseTerm(clamped);

                                    // Update slider
                                    if (leaseTermRangeRef.current) {
                                        leaseTermRangeRef.current.value = clamped;
                                        updateSliderProgress(leaseTermRangeRef.current);
                                    }
                                }
                            }}
                        />
                        {t("calculator.months")}
                    </div>

                    {/* <span>
                        {leaseTerm} {t("calculator.months")}
                    </span> */}
                </label>
                <input
                    ref={leaseTermRangeRef}
                    type="range"
                    min="12"
                    max="36"
                    value={leaseTerm}
                    onChange={(e) => setLeaseTerm(Number(e.target.value))}
                />
                <div className={styles.valueDisplay}>
                    <span>12 {t("calculator.months")}</span>

                    <span>36 {t("calculator.months")}</span>
                </div>
            </div>

            <motion.div className={styles.resultCard} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
                <div className={styles.resultCardHead}>
                    <div className={styles.resultCardHeadTitle}>
                        <div className={styles.resultCardItem}>
                            <h3>{t("calculator.totalDue")}</h3>
                            <div className={styles.resultPaymentsMain}>
                                {totalAmountDue.toLocaleString()}{" "}
                                <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M1 5.25C1 4.25544 1.39509 3.30161 2.09835 2.59835C2.80161 1.89509 3.75544 1.5 4.75 1.5C5.74456 1.5 6.69839 1.89509 7.40165 2.59835C8.10491 3.30161 8.5 4.25544 8.5 5.25V11.5M6 9H11M6 6.5H11"
                                        stroke="white"
                                        stroke-width="1.2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                        </div>

                        <div className={styles.resultCardItem}>
                            <div
                                className={styles.tooltipWrapper}
                                onMouseEnter={() => setHoveredTooltip("payments")}
                                onMouseLeave={() => setHoveredTooltip(null)}
                            >
                                <h3>{t("calculator.totalPayments")}</h3>
                                <svg
                                    className={styles.tooltip}
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 18C4.0293 18 0 13.9707 0 9C0 4.0293 4.0293 0 9 0C13.9707 0 18 4.0293 18 9C18 13.9707 13.9707 18 9 18ZM8.1 11.7V13.5H9.9V11.7H8.1ZM9.9 10.2195C10.6233 10.0015 11.2442 9.53096 11.6497 8.89357C12.0552 8.25618 12.2183 7.49435 12.1093 6.74682C12.0002 5.99929 11.6263 5.31579 11.0556 4.82081C10.4849 4.32583 9.75544 4.05228 9 4.05C8.27172 4.04977 7.56587 4.30191 7.00255 4.76349C6.43923 5.22507 6.05325 5.86759 5.9103 6.5817L7.6761 6.9354C7.72621 6.68469 7.84649 6.45336 8.02293 6.26833C8.19937 6.08331 8.42473 5.95219 8.67278 5.89023C8.92083 5.82828 9.18138 5.83804 9.4241 5.91837C9.66682 5.99871 9.88174 6.14631 10.0438 6.34402C10.206 6.54173 10.3086 6.7814 10.3398 7.03516C10.371 7.28892 10.3295 7.54632 10.2202 7.77742C10.1108 8.00853 9.9381 8.20383 9.72209 8.3406C9.50608 8.47737 9.25567 8.54998 9 8.55C8.7613 8.55 8.53239 8.64482 8.3636 8.8136C8.19482 8.98239 8.1 9.2113 8.1 9.45V10.8H9.9V10.2195Z"
                                        fill="white"
                                    />
                                </svg>

                                <AnimatePresence>
                                    {hoveredTooltip === "payments" && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.2 }}
                                            className={styles.tooltipBox}
                                        >
                                            {t("calculator.tooltipPayments")}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            <div className={styles.resultPayments}>
                                {totalPayments.toLocaleString()}{" "}
                                <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M1 5.25C1 4.25544 1.39509 3.30161 2.09835 2.59835C2.80161 1.89509 3.75544 1.5 4.75 1.5C5.74456 1.5 6.69839 1.89509 7.40165 2.59835C8.10491 3.30161 8.5 4.25544 8.5 5.25V11.5M6 9H11M6 6.5H11"
                                        stroke="white"
                                        stroke-width="1.2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                        </div>

                        <div className={styles.resultCardItem}>
                            <div
                                className={styles.tooltipWrapper}
                                onMouseEnter={() => setHoveredTooltip("interest")}
                                onMouseLeave={() => setHoveredTooltip(null)}
                            >
                                <h3>{t("calculator.totalInterest")}</h3>
                                <svg
                                    className={styles.tooltip}
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 18C4.0293 18 0 13.9707 0 9C0 4.0293 4.0293 0 9 0C13.9707 0 18 4.0293 18 9C18 13.9707 13.9707 18 9 18ZM8.1 11.7V13.5H9.9V11.7H8.1ZM9.9 10.2195C10.6233 10.0015 11.2442 9.53096 11.6497 8.89357C12.0552 8.25618 12.2183 7.49435 12.1093 6.74682C12.0002 5.99929 11.6263 5.31579 11.0556 4.82081C10.4849 4.32583 9.75544 4.05228 9 4.05C8.27172 4.04977 7.56587 4.30191 7.00255 4.76349C6.43923 5.22507 6.05325 5.86759 5.9103 6.5817L7.6761 6.9354C7.72621 6.68469 7.84649 6.45336 8.02293 6.26833C8.19937 6.08331 8.42473 5.95219 8.67278 5.89023C8.92083 5.82828 9.18138 5.83804 9.4241 5.91837C9.66682 5.99871 9.88174 6.14631 10.0438 6.34402C10.206 6.54173 10.3086 6.7814 10.3398 7.03516C10.371 7.28892 10.3295 7.54632 10.2202 7.77742C10.1108 8.00853 9.9381 8.20383 9.72209 8.3406C9.50608 8.47737 9.25567 8.54998 9 8.55C8.7613 8.55 8.53239 8.64482 8.3636 8.8136C8.19482 8.98239 8.1 9.2113 8.1 9.45V10.8H9.9V10.2195Z"
                                        fill="white"
                                    />
                                </svg>

                                <AnimatePresence>
                                    {hoveredTooltip === "interest" && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.2 }}
                                            className={styles.tooltipBox}
                                        >
                                            {t("calculator.tooltipInterest")}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                            <div className={styles.resultPayments}>
                                {totalInterest.toLocaleString()}{" "}
                                <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M1 5.25C1 4.25544 1.39509 3.30161 2.09835 2.59835C2.80161 1.89509 3.75544 1.5 4.75 1.5C5.74456 1.5 6.69839 1.89509 7.40165 2.59835C8.10491 3.30161 8.5 4.25544 8.5 5.25V11.5M6 9H11M6 6.5H11"
                                        stroke="white"
                                        stroke-width="1.2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className={styles.qrWrapper}>
                        <QRCode className={styles.qrSelf} value={appLinks[0].url} />
                        <p dangerouslySetInnerHTML={{ __html: t("calculator.qrText") }}></p>
                    </div>
                </div>

                <div className={styles.mobileApps}>
                    <a href={appLinks[1].url}>
                        <img src={appstore} alt="App Store" />
                    </a>
                    <a href={appLinks[2].url}>
                        <img src={googleplay} alt="Google Play" />
                    </a>
                </div>

                <p className={styles.note}>{t("calculator.disclaimer")}</p>
            </motion.div>
        </div>
    );
}

export default CreditCalculator;